<template>
  <div id="backtester-basic-options-tab">
    <!-- Number of Holdings -->
    <v-row mb-6>
      <v-col cols="12">
        <helpers-section-sub-header>Number of Holdings:</helpers-section-sub-header>
      </v-col>
      <v-col
        cols="12"
        sm="6"
        md="4"
        xl="2"
      >
        <v-select
          v-model="backtesterCriteria.holdings"
          :items="holdingsItems"
          attach
          menu-props="offsetY"
          hide-details
        >
          <template
            slot="item"
            slot-scope="props"
          >
            <v-chip
              v-if="!accessAllowed(props.item)"
              color="accent white--text"
              class="v-chip--x-small font-weight-bold ma-0 mr-2"
            >
              PRO
            </v-chip>
            <span>{{ props.item.text }}</span>
          </template>
        </v-select>
      </v-col>
    </v-row>

    <!-- How do you want stocks selected when rebalancing? -->
    <helpers-section-sub-header class="mb-4">
      How do you want stocks selected when rebalancing?
      <v-btn
        icon
        class="mx-0 mt-0"
        @click="stockSelectionHelpDialog = true"
      >
        <v-icon color="blue">
          info
        </v-icon>
      </v-btn>
    </helpers-section-sub-header>
    <v-container
      fluid
      grid-list-xl
      pa-0
    >
      <v-row>
        <!-- Radio group main -->
        <v-col
          cols="12"
          sm="8"
          md="6"
          xl="4"
        >
          <v-radio-group
            v-model="backtesterCriteria.stock_selection_group"
            class="v-radio-group--x-small ma-0 pa-0"
            hide-details
          >
            <template v-for="item in stockSelectionItems">
              <!-- Expand 'Absolute count among largest holdings' -->
              <template v-if="item.value === 3">
                <div
                  :key="item.value"
                  :class="{ 'grey lighten-4': item.value === backtesterCriteria.stock_selection_group }"
                >
                  <v-radio
                    :value="item.value"
                    color="primary"
                  >
                    <span
                      slot="label"
                      class="text-body-2"
                    >{{ item.text }}</span>
                  </v-radio>
                  <div
                    v-show="backtesterCriteria.stock_selection_group === 3"
                    class="pl-6 py-2"
                  >
                    <label class="text-caption grey--text">Top holdings to use from each fund</label>
                    <v-select attach
                      v-model="backtesterCriteria.stock_selection_top_ranking"
                      :items="topRankingItems"
                      menu-props="offsetY"
                      class="pt-0"
                      style="max-width: 100px"
                    />
                  </div>
                </div>
              </template>

              <!-- Expand 'Combined % of Portfolio w/ custom weight for each fund' -->
              <template v-else-if="item.value === 7">
                <div
                  :key="item.value"
                  :class="{ 'grey lighten-4': item.value === backtesterCriteria.stock_selection_group }"
                >
                  <v-radio
                    :value="item.value"
                    color="primary"
                  >
                    <span
                      slot="label"
                      class="text-body-2"
                    >{{ item.text }}</span>
                  </v-radio>
                  <div
                    v-show="backtesterCriteria.stock_selection_group === 7"
                    class="pl-6 py-2"
                  >
                    <v-btn
                      small
                      color="primary"
                      @click="customWeightDialog = true;"
                    >
                      Edit weights
                    </v-btn>
                  </div>
                </div>
              </template>

              <!-- v-else Non-expandable radios -->
              <v-radio
                v-else
                :key="item.value"
                :disabled="!accessAllowed(item)"
                :title="!accessAllowed(item) ? 'Pro Subscription Required For This Option' : ''"
                :value="item.value"
                :class="{ 'grey lighten-4': item.value === backtesterCriteria.stock_selection_group }"
                color="primary"
              >
                <template slot="label">
                  <v-chip
                    v-if="!accessAllowed(item)"
                    color="accent white--text"
                    class="v-chip--x-small font-weight-bold ma-0 mr-2"
                  >
                    PRO
                  </v-chip>
                  <span class="text-body-2">{{ item.text }}</span>
                </template>
              </v-radio>
            </template>
          </v-radio-group>
        </v-col>

        <!-- Optionally require a minimum threshold for stock selection -->
        <v-col
          v-if="backtesterCriteria.stock_selection_group !== 4 && backtesterCriteria.stock_selection_group !== 9"
          cols="12"
          sm="4"
          xl="3"
        >
          <h4 class="text-body-2 mb-2">
            Optionally require a minimum threshold for stock selection:
          </h4>
          <v-text-field
            v-model="backtesterCriteria.consensus_min_filers" :disabled="backtesterCriteria.stock_selection_group==9  || backtesterCriteria.stock_selection_group==4"
            mask="###"
            hint="Minimum number of filers holding stock"
            persistent-hint
          />
          <v-text-field
            v-model="backtesterCriteria.consensus_min_percent_portfolio"
            hint="Minimum % of portfolio per filer"
            persistent-hint
            suffix="%"
            @keypress="isNumber($event)"
          />

          <v-text-field
            v-model="backtesterCriteria.exclude_stocks_change_in_shares_decrease"
            hint="Exclude stock if QoQ decrease in shares held"
            persistent-hint
            @keypress="isNumber($event)"
          />
          <v-text-field
            v-model="backtesterCriteria.only_include_stocks_change_in_shares_increase"
            hint="Only include stock if QoQ increase in shares held"
            persistent-hint
            @keypress="isNumber($event)"
          />

          <v-text-field
            v-model="backtesterCriteria.max_quarters_held"
            hint="Exclude any manager's positions if those positions have been held for more than ___ quarters."
            persistent-hint
            @keypress="isNumber($event)"
          />
        </v-col>
      </v-row>
    </v-container>

    <!-- Stock weighting -->
    <v-row mt-6>
      <v-col cols="12">
        <helpers-section-sub-header class="mb-4">
          Stock weighting:
        </helpers-section-sub-header>
        <v-radio-group
          v-model="backtesterCriteria.stock_weight_group"
          hide-details
          class="v-radio-group--x-small"
        >
          <v-radio
            v-for="item in weightSelectionItems"
            :key="item.value"
            :value="item.value"
            :label="item.text"
            color="primary"
          />
        </v-radio-group>
      </v-col>
    </v-row>

    <!-- Stock selection help dialog -->
    <v-dialog
      v-model="stockSelectionHelpDialog"

      width="600px"
    >
      <v-card>
        <v-card-title class="d-block primary white--text">
          <h4 class="text-h6 mb-1">
            Stock Selection Options
          </h4>
          This section lets you choose how you want the stocks to be selected from each filer's holdings.
        </v-card-title>
        <v-card-text>
          <dl>
            <dt>Absolute count</dt>
            <dd>This will count the number of filers that hold at least one share of a stock for the quarter being rebalanced.</dd>
            <dt>Dollar value</dt>
            <dd>Takes the top securities ordered by market value from a combined portfolio of the list of filers.</dd>
            <dt>Combined Percent of Portfolio</dt>
            <dd>Takes the top securities by summing each securities percent of portfolio for each filer.</dd>
            <dt>Absolute count that appear among the largest 10 holdings</dt>
            <dd>Will look at the top XX largest holdings from each selected filer and then picks the most frequently held stocks (not necessarily the ones with the largest market value) among all of the selected filers.</dd>
            <dt>Top # Holdings by Dollar Value from each funds holdings</dt>
            <dd>Takes the top XX holdings from each filer's portfolio into a combined portfolio.</dd>
            <dt>Select only new buys during the quarter</dt>
            <dd>Looks at only new buys each quarter.  Will select the top new buys based on combined percent of portfolio.</dd>
            <dt>Combined % of Portfolio with custom weight for each individual fund</dt>
            <dd>Takes the top securities by summing each securities percent of portfolio for each filer.  You have the option to over or under-weight individual funds.</dd>
            <dt>Combined Percent Ownership</dt>
            <dd>Similar to the existing rule "Combined Percent of Portfolio". But instead of adding up the portfolio weights, this criteria adds up the %  values of all 13F filers in a group for a certain stock.</dd>
            <dt>Top # Holding(s) by Percent Ownership from each funds holdings</dt>
            <dd>Similar to the existing rule "Top # Holding(s) by %Dollar Value from each funds holding". But instead of selecting the X largest stock of each 13F filer the backtesting rule selects the X stocks of each filer that shows the highest % Ownership for each filer.</dd>
          </dl>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Custom weights dialog -->
    <v-dialog
      v-model="customWeightDialog"

      persistent
      width="700px"
    >
      <v-card>
        <v-card-title class="d-block primary white--text">
          <h4 class="text-h6 mb-1">
            Custom Weight Per Fund
          </h4>
          Scale of 1 through 10
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="!selectedGroup.filers.length"
            :value="true"
            color="error"
            icon="warning"
          >
            No filers have been added yet.
          </v-alert>
          <table v-else>
            <tr
              v-for="filer in selectedGroup.filers"
              :key="filer.id"
            >
              <td>
                <input
                  v-model="filer.weight"
                  type="number"
                  min="1"
                  max="10"
                  class="mr-4 text-center font-weight-bold"
                  style="border: solid 1px #aaa; width: 75px"
                >
              </td>
              <td>{{ filer.name }}</td>
            </tr>
          </table>
        </v-card-text>
        <v-card-actions>
          <v-btn
            text
            color="error"
            @click="ClearWeight()"
          >
            Reset Weight
          </v-btn>
          <v-spacer />
          <v-btn
            text
            color="primary"
            @click="customWeightDialog = false"
          >
            Done
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Modules
import { mapState } from 'vuex'
import { mapFields } from 'vuex-map-fields'

// Mixins
import AccessMixin from '@/mixins/access'

export default {
  mixins: [AccessMixin],

  data: () => ({
    stockSelectionHelpDialog: false,
    customWeightDialog: false,
    holdingsItems: [
      {
        text: '1 Holding',
        value: 5,
        requires_subscription: false
      },
      {
        text: '2 Holdings',
        value: 8,
        requires_subscription: false
      },
      {
        text: '3 Holdings',
        value: 4,
        requires_subscription: false
      },
      {
        text: '4 Holdings',
        value: 9,
        requires_subscription: false
      },
      {
        text: '5 Holdings',
        value: 3,
        requires_subscription: false
      },
      {
        text: '6 Holdings',
        value: 11,
        requires_subscription: false
      },
      {
        text: '7 Holdings',
        value: 12,
        requires_subscription: false
      },
      {
        text: '8 Holdings',
        value: 13,
        requires_subscription: false
      },
      {
        text: '9 Holdings',
        value: 14,
        requires_subscription: false
      },

      {
        text: '10 Holdings',
        value: 1,
        requires_subscription: false
      },
      {
        text: '11 Holdings',
        value: 15,
        requires_subscription: false
      },
      {
        text: '12 Holdings',
        value: 16,
        requires_subscription: false
      },
      {
        text: '13 Holdings',
        value: 17,
        requires_subscription: false
      },
      {
        text: '14 Holdings',
        value: 18,
        requires_subscription: false
      },
      {
        text: '15 Holdings',
        value: 10,
        requires_subscription: false
      },
      {
        text: '20 Holdings',
        value: 2,
        requires_subscription: false
      },
      {
        text: '30 Holdings',
        value: 6,
        requires_pro_subscription: true
      },
      {
        text: '40 Holdings',
        value: 19,
        requires_pro_subscription: true
      },
      {
        text: '50 Holdings',
        value: 7,
        requires_pro_subscription: true
      }
    ],
    stockSelectionItems: [
      {
        text: 'Absolute Count',
        value: 1,
        tooltip: 'Counts the number of filers holding at least one share of stock'
      },
      {
        text: 'Dollar Value',
        value: 2,
        tooltip: 'Sums the total market values of each stock and returns the top picks'
      },
      {
        text: 'Combined % of Portfolio',
        value: 6,
        tooltip: 'Sums a given stocks percent of portfolio for the funds selected.'
      },
      {
        text: 'Absolute count among largest holdings',
        value: 3,
        tooltip: "Counts the number of filers holding at least one share of stock, but limits the stock selection to a fund's top XX number of holdings."
      },
      {
        text: "Top Holding(s) by $ from each fund's holdings",
        value: 4,
        tooltip: 'Takes each individual funds top holdings'
      },
      {
        text: 'Select only new buys during quarter',
        value: 5,
        tooltip: 'Takes the top stocks ordered by summing the percent of portfolio, limits stocks to new buys during the quarter only.'
      },
      {
        text: 'Combined % of Portfolio w/ custom weight for each fund',
        value: 7,
        tooltip: "Sums a given stocks percent of portfolio for the funds selected, but let's you overweight/underweight individual funds contributions"
      },
      {
        text: 'Combined Percent Ownership',
        value: 8,
        tooltip: 'Sums the percent ownership of each fund selected.',
        requires_pro_subscription: true
      },
      {
        text: "Top Holding(s) by % Ownership from each fund's holdings",
        value: 9,
        tooltip: 'Takes each individual funds top % owned holdings',
        requires_pro_subscription: true
      }
    ],
    topRankingItems: [
      { text: '5', value: '1' },
      { text: '10', value: '2' },
      { text: '20', value: '3' },
      { text: '30', value: '4' },
      { text: '40', value: '5' },
      { text: '50', value: '6' }
    ],
    weightSelectionItems: [
      {
        text: 'Manager Weight Stocks',
        value: 1,
        tooltip: 'Short instructions here'
      },
      {
        text: 'Equally Weight Stocks',
        value: 2,
        tooltip: 'Short instructions here'
      }
    ]
  }),

  computed: {
    ...mapState('app', ['backtesterCriteria']),
    ...mapFields({
      backtesterCriteria: 'app.backtesterCriteria',
      selectedGroup: 'app.selectedGroup'
    })
  },

  watch: {
    customWeightDialog (val) {
      if (val) {
        this.selectedGroup.filers.forEach(filer => {
          if (!filer.hasOwnProperty('weight')) this.$set(filer, 'weight', 1)
        })
      }
    },
    '$store.state.init' (val) {
      this.holdingsItems.forEach(holding => {
        this.$set(holding, 'disabled', !this.accessAllowed(holding))
      })
    }
  },

  created () {
    this.holdingsItems.forEach(holding => {
      this.$set(holding, 'disabled', !this.accessAllowed(holding))
    })
  },

  methods: {
    ClearWeight () {
      this.selectedGroup.filers.forEach(filer => {
        this.$set(filer, 'weight', 1)
      })
    },
    isNumber (evt) {
      evt = (evt) || window.event
      var charCode = (evt.which) ? evt.which : evt.keyCode
      if ((charCode > 31 && (charCode < 48 || charCode > 57)) && charCode !== 46) {
        evt.preventDefault()
      } else {
        return true
      }
    }
  }
}
</script>
